import type {MRT_ColumnDef} from 'mantine-react-table';
import {useMemo} from 'react';
import type {NotificationRuleSimpleResponse} from 'src/api/ProactApiClient';
import {ModuleEnum, NotificationRuleTypeEnum} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {
  ActiveStateDataOptions,
  NotificationRuleModuleDataOptions,
  NotificationRuleTypeDataOptions,
} from 'src/shared/constants/columnFilterData';

const getModuleLabel = (module: ModuleEnum | undefined): string => {
  switch (module) {
    case ModuleEnum.Actions:
      return 'Actions';
    case ModuleEnum.Approvals:
      return 'Approvals';
    case ModuleEnum.AssignedForms:
      return 'Assigned Forms';
    case ModuleEnum.Attachments:
      return 'Attachments';
    case ModuleEnum.ToolboxTalks:
      return 'Toolbox Talks';
    case ModuleEnum.Forms:
      return 'Forms';
    case ModuleEnum.Training:
      return 'Training';
    case ModuleEnum.ItemTrackerAssignments:
      return 'Item Tracker';
    case ModuleEnum.SafetyEvent:
      return 'Safety Event';
    default:
      return '';
  }
};

const getRuleTypeLabel = (ruleType: NotificationRuleTypeEnum): string => {
  switch (ruleType) {
    case NotificationRuleTypeEnum.Reminder:
      return 'Reminder';
    case NotificationRuleTypeEnum.ChangeEvent:
      return 'Change Event';
    case NotificationRuleTypeEnum.Schedule:
      return 'Schedule';
    default:
      return '';
  }
};

export const useNotificationRulesColumns = () => {
  const columns = useMemo<MRT_ColumnDef<NotificationRuleSimpleResponse>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'active',
        header: 'Status',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: ActiveStateDataOptions,
        },
        Cell: ({cell}) => {
          const isActive = cell.getValue<boolean>();
          return (
            <Pill
              text={isActive ? 'Active' : 'Inactive'}
              color={isActive ? 'green' : 'yellow'}
            />
          );
        },
      },
      {
        accessorKey: 'ruleType',
        header: 'Type',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: NotificationRuleTypeDataOptions,
        },
        Cell: ({cell}) => {
          const ruleType = cell.getValue<NotificationRuleTypeEnum>();
          const label = getRuleTypeLabel(ruleType);
          return label;
        },
      },
      {
        accessorKey: 'module',
        header: 'Module',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: NotificationRuleModuleDataOptions,
        },
        Cell: ({cell}) => {
          const module = cell.getValue<ModuleEnum | undefined>();
          const label = getModuleLabel(module);
          return label;
        },
      },
      {
        accessorKey: 'project.name',
        header: 'Project',
      },
    ],
    []
  );

  return columns;
};
